import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PixelGlyph } from './PixelGlyph';
import { engagements, engagementsStatement } from '../data/landing';

// Width of the flush-left visual column (rows and header spacer share it)
const VISUAL_W = 'w-[80px] md:w-[128px]';
const VISUAL_H = 'h-[80px] md:h-[128px]';
export function Engagements() {
  return <section id="engagements" aria-labelledby="engagements-heading" className="bg-bg px-page py-20 md:py-28" data-id="element-73" data-mp="2jia333edex3y">
      <h2 id="engagements-heading" className="sr-only" data-id="element-74" data-mp="2ejysgqg94iko">
        Signature engagements
      </h2>

      <p className="font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] text-fg md:text-[48px]" data-id="element-75" data-mp="29isykpauup9k">
        {engagementsStatement.map((segment, i) => segment.strong ? <span key={i} className="text-fg/40" data-id="element-76" data-mp="y5qidvco41px">
              {segment.text}
            </span> : <span key={i} data-id="element-77" data-mp="t4t2xqj1lls4">{segment.text}</span>)}
      </p>

      {/* Column headings */}
      <div className="bleed-x mt-14 hidden !px-0 pb-3 text-[12px] font-medium uppercase tracking-[0.04em] text-fg/40 md:mt-16 md:flex" aria-hidden="true" data-id="element-78" data-mp="25d9fg3m0pkn6">
        <span className={`${VISUAL_W} ml-[var(--guide)] shrink-0`} data-id="element-79" data-mp="1veshgmonw5rm" />
        <div className="grid flex-1 grid-cols-12 gap-[var(--gutter)] pl-6 pr-[var(--page-x)]" data-id="element-80" data-mp="1vgmyhpwtbdqf">
          <span className="col-span-4 pl-10" data-id="element-81" data-mp="20fq0t2ler5up">Engagement</span>
          <span className="col-span-6" data-id="element-82" data-mp="1ml73h5kbmsj9">What it is</span>
          <span className="col-span-2 text-right" data-id="element-83" data-mp="1rcynex8z200f">Timeline</span>
        </div>
      </div>

      {/* Rows: hairlines run edge to edge; visual is flush to the left guide and both hairlines */}
      <ol className="bleed-x mt-10 border-t border-line !px-0 md:mt-0" data-id="element-84" data-mp="1cm7xxvuurd0h">
        {engagements.map((item, i) => <li key={item.title} className="border-b border-line" data-id="element-85" data-mp="13nmhq7cusa2v" data-mpi={i}>
            <Link to={item.title === 'Equity Partnership' ? '/contact?topic=equity' : '/contact'} className="group relative flex items-start transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-fg/[0.015] focus:outline-none focus-visible:bg-fg/[0.015]" data-id="element-86" data-mp="13ph31ms1e47h">
              {/* Square visual — row height equals its width so it meets both hairlines */}
              <div className={`${VISUAL_W} ml-[var(--guide)] shrink-0`} data-id="element-87" data-mp="30jh1ejfiw74v">
                <div className={`${VISUAL_H} relative w-full overflow-hidden`} data-id="element-88" data-mp="2vobepieaqofq">
                  <PixelGlyph shape={item.glyph} data-id="element-89" data-mp="3t964d2bsmh94" />
                </div>
                {/* Phones: number sits under the visual, freeing the text column */}
                <span className="block pb-4 pt-2 text-center text-[13px] tabular-nums text-fg/40 md:hidden" data-id="element-90" data-mp="3jcb9quin5104">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>

              <div className="grid min-h-[104px] flex-1 grid-cols-1 content-center gap-3 py-5 pl-5 pr-[var(--page-x)] md:min-h-[128px] md:grid-cols-12 md:items-center md:gap-[var(--gutter)] md:py-0 md:pl-6" data-id="element-91" data-mp="27wwcdpmis74h">
              {/* Number, title, audience */}
              <div className="flex items-center gap-4 md:col-span-4" data-id="element-92" data-mp="jf39h4kxwc1j">
                <span className="hidden w-6 shrink-0 text-[13px] tabular-nums text-fg/40 transition-colors duration-200 group-hover:text-fg md:block" data-id="element-93" data-mp="nebyjw51blqr">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:translate-x-1.5 group-focus-visible:translate-x-1.5" data-id="element-94" data-mp="x6hpr3ngpz4w">
                  <h3 className="text-[22px] leading-[1.2] tracking-[-0.01em] text-fg md:text-[24px]" data-id="element-95" data-mp="172bbndn2n4zc">{item.title}</h3>
                  <p className="mt-1 text-[14px] text-fg/50" data-id="element-96" data-mp="1b5zjzyjj24ln">{item.bestFor}</p>
                </div>
              </div>

              {/* Description */}
              {/* Width capped so every description wraps to two balanced lines */}
              <div className="md:col-span-6 md:pr-6" data-id="element-97" data-mp="3cyfdgovci2ug">
                <p className="max-w-[460px] text-[15px] leading-[1.55] text-fg/60 transition-colors duration-200 [text-wrap:balance] group-hover:text-fg/85" data-id="element-98" data-mp="3hw783qndk32c">
                  {item.description}
                </p>
              </div>

              {/* Timeline + action */}
              <div className="flex items-center justify-between gap-4 md:col-span-2 md:justify-end" data-id="element-99" data-mp="2wjoqks5y08q0">
                <span className="text-[14px] tabular-nums text-fg" data-id="element-100" data-mp="2rjit2vczrs00">
                  <span className="text-fg/40 md:hidden" data-id="element-101" data-mp="2ri7powngkm9f">Timeline · </span>
                  {item.duration}
                </span>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control border border-line text-fg/50 transition-colors duration-200 group-hover:border-fg/30 group-hover:bg-fg/[0.05] group-hover:text-fg group-focus-visible:border-fg/30 group-focus-visible:bg-fg/[0.05] group-focus-visible:text-fg" aria-hidden="true" data-id="element-102" data-mp="1yw68uruazw9v">
                  <ArrowUpRightIcon className="h-4 w-4" data-id="element-103" data-mp="1g5ovbui1l793" />
                </span>
              </div>
              </div>
            </Link>
          </li>)}
      </ol>

      <p className="mt-8 text-[14px] text-fg/55" data-id="element-104" data-mp="dkzzzj8ixol">
        Not sure which fits?{' '}
        <Link to="/contact" className="text-fg underline decoration-fg/30 underline-offset-4 transition-colors duration-150 hover:decoration-fg" data-id="element-105" data-mp="fe71fxrfjx4">
          Tell us where you stand
        </Link>{' '}
        and we will structure one around you.
      </p>
    </section>;
}